import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { useCreateProjectMutation } from "../../../../features/projectApi";
import { addBannerWithTimeout } from "../../../lib/centralizedBanner/slice";
import { useAppDispatch, useAppSelector } from "../../../lib/redux/hooks";
import { Layout } from "../../../ui/layout";
import Button from "../../../ui/molecules/button";
import { Spinner } from "../../../ui/molecules/spinner";
import { MenuItems } from "../../../ui/organisms/navbar/dto";
import { TabMenu } from "../../../ui/organisms/tabs/tabMenu";
import { TabPages } from "../../../ui/organisms/tabs/tabPages";
import { UserRole } from "../../users/dto";
import { findClients } from "../../users/slice";
import { Form } from "./form";
import { ProjectCreationFormData, projectCreationSchema } from "./validation";

export function NewProject() {
  const usersState = useAppSelector((state) => state.users);
  const dispatch = useAppDispatch();
  const navigation = useNavigate();

  const [direction, setDirection] = useState<"left" | "right">("left");
  const [tabValue, setTabValue] = useState<number>(0);

  const [createProject, { isLoading: isCreatingProject }] = useCreateProjectMutation();

  const form = useForm<ProjectCreationFormData>({
    resolver: zodResolver(projectCreationSchema),
    defaultValues: {
      name: "",
      description: "",
      customerId: "",
      commercialIds: [],
      teamLeaderId: "",
    },
  })

  useEffect(() => {
    try {
      const savedData = sessionStorage.getItem('newProjectFormData');
      if (savedData) {
        const parsedData = JSON.parse(savedData);
        Object.keys(parsedData).forEach(key => {
          if (parsedData[key] !== undefined && parsedData[key] !== null) {
            form.setValue(key as keyof ProjectCreationFormData, parsedData[key]);
          }
        });
      }
    } catch (error) {
      dispatch(addBannerWithTimeout({
        variant: "error",
        message: "Errore nel ripristino dei dati del form"
      }));
    }
  }, []);

  const handleCreateProject = async () => {
    try {
      const response = await createProject(form.getValues()).unwrap();
      sessionStorage.removeItem('newProjectFormData');
      dispatch(addBannerWithTimeout({
        variant: "success",
        message: "Progetto creato con successo"
      }))
      navigation(
        "/edit-project/" + response.projectId + "/target-lists"
      );
    } catch (error) {
      dispatch(addBannerWithTimeout({
        variant: "error",
        message: "Errore durante la creazione del progetto"
      }))
    }
  }

  const formValues = form.watch();
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (formValues.name || formValues.description || formValues.customerId ||
        (formValues.commercialIds && formValues.commercialIds.length > 0) || formValues.teamLeaderId) {
        sessionStorage.setItem('newProjectFormData', JSON.stringify(formValues));
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [formValues]);

  useEffect(() => {
    if (usersState.createFromProject && usersState.createFromProject.userId) {
      const role = usersState.createFromProject.userRole;
      if (role === UserRole.team_leader) {
        form.setValue("teamLeaderId", usersState.createFromProject.userId, { shouldValidate: true });
      };
      if (role === UserRole.commercial) {
        form.setValue("commercialIds", [...form.getValues("commercialIds"), usersState.createFromProject.userId], { shouldValidate: true });
      }
      if (role === UserRole.customer) {
        form.setValue("customerId", usersState.createFromProject.userId, { shouldValidate: true });
      }
    }
  }, [usersState.createFromProject]);

  useEffect(() => {
    return () => {
      if (!usersState.createFromProject) {
        setTimeout(() => {
          const currentPath = window.location.pathname;
          if (!currentPath.includes('/projects/new') && !currentPath.includes('/users/new')) {
            sessionStorage.removeItem('newProjectFormData');
          }
        }, 100);
      }
    };
  }, [usersState.createFromProject]);

  useEffect(() => {
    dispatch(findClients());
  }, []);

  return (
    <Layout
      menuItem={MenuItems.PROJECTS}
      breadcrumbItems={["Progetti", "Nuovo progetto"]}
      headerLabel={"Nuovo progetto"}
      headerBackPath={"/projects"}
      headerChildren={
        <div className="flex items-center h-[100%] w-[100%] justify-between">
          <TabMenu
            direction={direction}
            setDirection={(value) => setDirection(value)}
            setValue={(e) => {
              setTabValue(e);
            }}
            value={tabValue}
            items={[{
              icon: <></>,
              label: "Specifiche",
            },
            {
              icon: <></>,
              disabled: true,
              label: "Liste target (0)",
            },
            ]}
          />
          {
            isCreatingProject ? (
              <Spinner />
            ) : (
              <Button
                size={"sm"}
                iconPosition={"off"}
                label="Crea progetto"
                variant={"solid"}
                color={"blue"}
                onClick={() => form.handleSubmit(handleCreateProject)()}
              />
            )}
        </div>
      }
    >
      <TabPages
        direction={direction}
        setDirection={(value) => setDirection(value)}
        pages={[
          <Form key={0} form={form} />,
          <div key={1} className="h-full"></div>,
        ]}
        value={tabValue}
        setValue={(e) => {
          setTabValue(e);
        }}
      />
    </Layout>
  );
}
